import "ag-grid-community/styles/ag-grid.css"; // Mandatory CSS required by the grid
import "ag-grid-community/styles/ag-theme-quartz.css"; // Optional Theme applied to the grid
import { AgGridReact } from "ag-grid-react";
import axios from "axios";
import React, { useEffect, useState, useContext, useMemo } from "react";
import "../../node_modules/react-vis/dist/style.css";
import "../css/home.css";
import Constants from "../util/Constants";
import Utils from "../util/Utils";
import { UserContext } from "../main/MainHome.js";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined";

export default function PortfolioPerformance({ onOpenPortfolio }) {
  const [portfolios, setPortfolios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const defaultColDef = useMemo(
    () => ({
      resizable: true,
      floatingFilter: true,
      sortable: true,
      minWidth: 120,
    }),
    []
  );

  const overlayLoadingTemplate =
    '<span class="ag-overlay-loading-center">Please wait while data loading</span>';

  const userEmail = useContext(UserContext);

  const columnDefs = useMemo(() => {
    const PortfolioNameCell = (cellProps) => {
      const display = cellProps.value;
      if (!display) return null;
      if (!onOpenPortfolio) {
        return (
          <Typography component="span" variant="body2" sx={{ fontWeight: 600 }}>
            {display}
          </Typography>
        );
      }
      return (
        <Link
          component="button"
          type="button"
          variant="body2"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onOpenPortfolio(cellProps.data);
          }}
          sx={{
            cursor: "pointer",
            fontWeight: 600,
            textAlign: "left",
            p: 0,
            border: "none",
            background: "none",
            font: "inherit",
            verticalAlign: "baseline",
            color: "primary.main",
            "&:hover": { color: "primary.dark" },
          }}
        >
          {display}
        </Link>
      );
    };

    return [
      {
        headerName: "Portfolio Name",
        field: "name",
        filter: "agTextColumnFilter",
        sortable: true,
        width: 280,
        pinned: "left",
        cellRenderer: PortfolioNameCell,
        tooltipField: "name",
      },
      {
        headerName: "Perf from previous Day",
        field: "perfDay",
        filter: "agNumberColumnFilter",
        sortable: true,
        width: 200,
        valueFormatter: Utils.ag_PercentFormatter,
        cellStyle: Utils.ag_grid_cell_color_neg_val,
      },
      {
        headerName: "Category",
        field: "type",
        filter: "agTextColumnFilter",
        sortable: true,
        width: 200,
      },
      {
        headerName: "Perf to Week",
        field: "perfWeek",
        filter: "agNumberColumnFilter",
        sortable: true,
        width: 150,
        valueFormatter: Utils.ag_PercentFormatter,
        cellStyle: Utils.ag_grid_cell_color_neg_val,
      },
      {
        headerName: "Perf to Month",
        field: "perfMonth",
        filter: "agNumberColumnFilter",
        sortable: true,
        width: 150,
        valueFormatter: Utils.ag_PercentFormatter,
        cellStyle: Utils.ag_grid_cell_color_neg_val,
      },
      {
        headerName: "Perf to YTD",
        field: "perfYTD",
        filter: "agNumberColumnFilter",
        sortable: true,
        width: 150,
        valueFormatter: Utils.ag_PercentFormatter,
        cellStyle: Utils.ag_grid_cell_color_neg_val,
      },
      {
        headerName: "Perf to Year",
        field: "perfYear",
        filter: "agNumberColumnFilter",
        sortable: true,
        width: 150,
        valueFormatter: Utils.ag_PercentFormatter,
        cellStyle: Utils.ag_grid_cell_color_neg_val,
      },
      {
        headerName: "5Y (Annualized)",
        headerTooltip: "Compound annual growth rate over 5 years",
        field: "perf5Year",
        filter: "agNumberColumnFilter",
        sortable: true,
        width: 150,
        valueFormatter: Utils.ag_PercentFormatter,
        cellStyle: Utils.ag_grid_cell_color_neg_val,
      },
      {
        headerName: "10Y (Annualized)",
        headerTooltip: "Compound annual growth rate over 10 years",
        field: "perf10Year",
        filter: "agNumberColumnFilter",
        sortable: true,
        width: 150,
        valueFormatter: Utils.ag_PercentFormatter,
        cellStyle: Utils.ag_grid_cell_color_neg_val,
      },
      {
        headerName: "5Y (Actual return)",
        headerTooltip: "Total return over 5 years (not annualized)",
        field: "perf5YearTotal",
        filter: "agNumberColumnFilter",
        sortable: true,
        width: 160,
        valueFormatter: Utils.ag_PercentFormatter,
        cellStyle: Utils.ag_grid_cell_color_neg_val,
      },
      {
        headerName: "10Y (Actual return)",
        headerTooltip: "Total return over 10 years (not annualized)",
        field: "perf10YearTotal",
        filter: "agNumberColumnFilter",
        sortable: true,
        width: 165,
        valueFormatter: Utils.ag_PercentFormatter,
        cellStyle: Utils.ag_grid_cell_color_neg_val,
      },
    ];
  }, [onOpenPortfolio]);

  useEffect(() => {
    if (!userEmail) {
      setLoading(false);
      setLoadError("Sign in to load the leaderboard.");
      setPortfolios([]);
      return;
    }

    setLoading(true);
    setLoadError(null);
    const urlVal = Constants.PERFORMANCE_URL + "?userEmail=" + userEmail;
    axios({
      method: "get",
      responseType: "json",
      url: urlVal,
    })
      .then((response) => {
        setPortfolios(response.data?.portfolios || []);
      })
      .catch(() => {
        setLoadError("Could not load leaderboard data. Please try again later.");
        setPortfolios([]);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [userEmail]);

  return (
    <Box
      sx={{
        minHeight: "72vh",
        py: { xs: 2, sm: 3 },
        px: { xs: 1, sm: 2 },
        mb: "56px",
        background:
          "linear-gradient(180deg, rgba(13, 27, 42, 0.04) 0%, rgba(13, 27, 42, 0) 28%), linear-gradient(135deg, rgba(25, 118, 210, 0.06) 0%, rgba(212, 160, 23, 0.05) 100%)",
      }}
    >
      <Container maxWidth="xl">
        <Stack spacing={2.5}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems={{ xs: "flex-start", sm: "center" }}
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <EmojiEventsOutlinedIcon
                sx={{ fontSize: 40, color: "primary.main", opacity: 0.9 }}
                aria-hidden
              />
              <Box>
                <Typography
                  variant="h5"
                  component="h1"
                  sx={{
                    fontWeight: 700,
                    letterSpacing: "-0.02em",
                    color: "text.primary",
                  }}
                >
                  Portfolio Leaderboard
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, maxWidth: 560 }}>
                  Compare returns across curated portfolios. Click a portfolio name to open its
                  holdings screener.
                </Typography>
              </Box>
            </Stack>
            {!loading && portfolios.length > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                {portfolios.length} portfolio{portfolios.length === 1 ? "" : "s"}
              </Typography>
            )}
          </Stack>

          {loadError && (
            <Alert severity="warning" sx={{ borderRadius: 2 }}>
              {loadError}
            </Alert>
          )}

          <Card
            elevation={0}
            sx={{
              borderRadius: 2,
              border: 1,
              borderColor: "divider",
              overflow: "hidden",
              boxShadow: "0 8px 32px rgba(13, 27, 42, 0.08)",
            }}
          >
            <CardContent sx={{ p: { xs: 1, sm: 2 }, "&:last-child": { pb: { xs: 1, sm: 2 } } }}>
              <Box sx={{ position: "relative" }}>
                {loading && (
                  <Box
                    sx={{
                      position: "absolute",
                      inset: 0,
                      zIndex: 2,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: "rgba(255,255,255,0.72)",
                      borderRadius: 1,
                    }}
                  >
                    <CircularProgress size={36} />
                  </Box>
                )}
                <Box
                  className="ag-theme-quartz"
                  sx={{
                    width: "100%",
                    height: { xs: "62vh", md: "70vh" },
                    "& .ag-root-wrapper": { borderRadius: 1 },
                  }}
                >
                  <AgGridReact
                    defaultColDef={defaultColDef}
                    columnDefs={columnDefs}
                    rowData={portfolios}
                    pagination={true}
                    paginationPageSize={20}
                    paginationPageSizeSelector={[10, 20, 50, 100]}
                    autoSizePadding={true}
                    overlayLoadingTemplate={overlayLoadingTemplate}
                    enableCellTextSelection
                    tooltipShowDelay={400}
                  />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Stack>
      </Container>
    </Box>
  );
}
